import { useEffect, useMemo, useState } from "react";
import { Check, CheckCircle2, Mail, Sparkles, X } from "lucide-react";
import type { FilterDto, LabelMapDto } from "../../../shared/types";
import { badgeClass, SWATCH_CLASSES } from "../filterColors";

type RunState = "select" | "running" | "complete" | "error";

interface FilterResult {
  filter: FilterDto;
  matched: number;
}

export function countFilterMatches(
  labels: LabelMapDto,
  messageIds: string[],
  filterId: number,
): number {
  let count = 0;
  for (const id of new Set(messageIds)) {
    if (labels[id]?.includes(filterId)) count += 1;
  }
  return count;
}

export function RunFiltersDialog({
  filters,
  messageIds,
  onRun,
  onClose,
}: {
  filters: FilterDto[];
  messageIds: string[];
  onRun: (filterIds: number[]) => Promise<LabelMapDto>;
  onClose: () => void;
}) {
  const enabled = useMemo(() => filters.filter((filter) => filter.enabled), [filters]);
  const [selected, setSelected] = useState(() => new Set(enabled.map((filter) => filter.id)));
  const [state, setState] = useState<RunState>("select");
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<FilterResult[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && state !== "running") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, state]);

  const toggle = (id: number) => {
    setSelected((previous) => {
      const next = new Set(previous);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const run = async () => {
    if (!selected.size || !messageIds.length) return;
    setState("running");
    setError(null);
    setProgress(7);
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      setProgress((current) => Math.min(91, current + Math.max(1, Math.round((92 - current) / 9))));
    }, 260);

    try {
      const labels = await onRun([...selected]);
      const minimumAnimation = Math.max(0, 900 - (Date.now() - startedAt));
      if (minimumAnimation) await new Promise((resolve) => setTimeout(resolve, minimumAnimation));
      setResults(
        enabled
          .filter((filter) => selected.has(filter.id))
          .map((filter) => ({
            filter,
            matched: countFilterMatches(labels, messageIds, filter.id),
          })),
      );
      setProgress(100);
      setState("complete");
    } catch (runError) {
      setError(runError instanceof Error ? runError.message : "The filters could not be run.");
      setState("error");
    } finally {
      window.clearInterval(timer);
    }
  };

  const allSelected = enabled.length > 0 && selected.size === enabled.length;
  const stage = progress < 28 ? "Preparing messages" : progress < 78 ? "Finding matches" : "Saving results";

  return (
    <div
      className="fixed inset-0 z-[70] grid place-items-center bg-[#111820]/50 p-4 backdrop-blur-[2px]"
      onMouseDown={(e) => {
        if (state !== "running" && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="run-filters-title"
        className="w-[540px] max-w-full overflow-hidden rounded-lg border border-[#d9dee4] bg-white shadow-[0_24px_70px_rgb(20_31_43/28%)]"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="flex items-start gap-3 border-b border-stroke px-5 py-4">
          <span className="grid size-10 shrink-0 place-items-center rounded-md bg-[#e8f2fb] text-accent">
            <Sparkles size={19} />
          </span>
          <div className="min-w-0 flex-1">
            <h2 id="run-filters-title" className="text-[16px] font-semibold">
              {state === "complete" ? "Filtering complete" : "Run mail filters"}
            </h2>
            <p className="mt-0.5 text-[12px] text-ink-muted">
              {state === "complete"
                ? `${messageIds.length} loaded email${messageIds.length === 1 ? "" : "s"} checked`
                : "Choose which filters should check the emails currently loaded."}
            </p>
          </div>
          {state !== "running" && (
            <button
              onClick={onClose}
              aria-label="Close"
              title="Close"
              className="grid size-8 place-items-center rounded-md text-ink-muted hover:bg-black/5 hover:text-ink"
            >
              <X size={17} />
            </button>
          )}
        </header>

        {(state === "select" || state === "error") && (
          <>
            <div className="flex items-center justify-between border-b border-stroke bg-[#fafbfc] px-5 py-2.5">
              <span className="flex items-center gap-1.5 text-[12px] text-ink-muted">
                <Mail size={14} /> {messageIds.length} email{messageIds.length === 1 ? "" : "s"} ready
              </span>
              <button
                onClick={() => setSelected(allSelected ? new Set() : new Set(enabled.map((filter) => filter.id)))}
                className="text-[12px] font-semibold text-accent hover:text-accent-hover"
              >
                {allSelected ? "Clear all" : "Select all"}
              </button>
            </div>

            <div className="max-h-[340px] overflow-y-auto px-5">
              {enabled.map((filter) => {
                const checked = selected.has(filter.id);
                return (
                  <button
                    key={filter.id}
                    onClick={() => toggle(filter.id)}
                    role="checkbox"
                    aria-checked={checked}
                    className="flex w-full items-start gap-3 border-b border-stroke py-3.5 text-left last:border-0 hover:bg-[#fafbfc]"
                  >
                    <span
                      className={`mt-0.5 grid size-5 shrink-0 place-items-center rounded border ${
                        checked ? "border-accent bg-accent text-white" : "border-[#b8c0c8] bg-white"
                      }`}
                    >
                      {checked && <Check size={13} strokeWidth={3} />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <i className={`size-2.5 shrink-0 rounded-full ${SWATCH_CLASSES[filter.color]}`} />
                        <b className="text-[13px] font-semibold">{filter.name}</b>
                      </span>
                      <span className="mt-1 block line-clamp-2 text-[12px] leading-5 text-ink-muted">
                        {filter.prompt}
                      </span>
                    </span>
                  </button>
                );
              })}
              {!enabled.length && (
                <p className="py-10 text-center text-[13px] text-ink-muted">
                  No enabled filters. Enable or create one in Settings.
                </p>
              )}
            </div>

            {error && (
              <p className="mx-5 mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-800">
                {error}
              </p>
            )}
            <footer className="flex items-center justify-between border-t border-stroke bg-[#fafbfc] px-5 py-3.5">
              <span className="text-[12px] text-ink-muted">
                {selected.size} of {enabled.length} selected
              </span>
              <div className="flex gap-2">
                <button onClick={onClose} className="rounded-md border border-stroke bg-white px-4 py-2 text-[13px] hover:bg-black/[0.03]">
                  Cancel
                </button>
                <button
                  onClick={run}
                  disabled={!selected.size || !messageIds.length}
                  className="flex items-center gap-2 rounded-md bg-accent px-4 py-2 text-[13px] font-semibold text-white hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-45"
                >
                  <Sparkles size={14} /> Run selected
                </button>
              </div>
            </footer>
          </>
        )}

        {state === "running" && (
          <div className="flex min-h-[390px] flex-col items-center justify-center px-8 py-10 text-center">
            <div className="filter-run-animation" aria-hidden="true">
              <span className="filter-run-orbit"><Sparkles size={18} /></span>
              <span className="filter-run-core"><Mail size={28} /></span>
            </div>
            <h3 className="mt-7 text-[17px] font-semibold">{stage}</h3>
            <p className="mt-1.5 max-w-[330px] text-[12px] leading-5 text-ink-muted">
              Running {selected.size} filter{selected.size === 1 ? "" : "s"} across {messageIds.length} loaded email{messageIds.length === 1 ? "" : "s"}.
            </p>
            <div className="mt-7 h-2 w-full max-w-[360px] overflow-hidden rounded-full bg-[#e8edf2]">
              <div className="filter-run-progress h-full rounded-full bg-accent" style={{ width: `${progress}%` }} />
            </div>
            <span className="mt-2 text-[12px] font-semibold tabular-nums text-accent">{progress}%</span>
          </div>
        )}

        {state === "complete" && (
          <>
            <div className="px-5 py-5">
              <div className="mb-4 flex items-center gap-3 rounded-md bg-green-50 px-3.5 py-3 text-green-800">
                <CheckCircle2 size={20} />
                <span className="text-[13px] font-semibold">Your inbox badges are up to date.</span>
              </div>
              <div className="overflow-hidden rounded-md border border-stroke">
                {results.map(({ filter, matched }) => (
                  <div key={filter.id} className="flex items-center gap-3 border-b border-stroke px-3.5 py-3 last:border-0">
                    <span className={`rounded px-2 py-0.5 text-[11px] font-semibold ${badgeClass(filter.color)}`}>
                      {filter.name}
                    </span>
                    <span className="flex-1 text-[12px] text-ink-muted">matched</span>
                    <strong className="text-[14px] tabular-nums">{matched}</strong>
                    <span className="w-11 text-[12px] text-ink-muted">email{matched === 1 ? "" : "s"}</span>
                  </div>
                ))}
              </div>
            </div>
            <footer className="flex justify-end border-t border-stroke bg-[#fafbfc] px-5 py-3.5">
              <button onClick={onClose} autoFocus className="rounded-md bg-[#17202a] px-5 py-2 text-[13px] font-semibold text-white hover:bg-[#293746]">
                Done
              </button>
            </footer>
          </>
        )}
      </div>
    </div>
  );
}
